import {BaseApi} from './BaseApi'
import {Site} from './siteApi'
import {sanitizePayload} from '../utils/apiUtils'
import {reportCatchError} from '../utils/errorReporter'
import type {UpsertTeamRequest} from './teamApi'

// Enums matching MSP NodeJS service
export enum IndustryType {
  Light_Industrial = 'Light Industrial',
  Manufacturing = 'Manufacturing',
  Warehouse_Distribution = 'Warehouse & Distribution',
  Construction = 'Construction',
  Skilled_Trades = 'Skilled Trades',
  Janitorial_Facilities = 'Janitorial / Facilities',
  Landscaping = 'Landscaping',
  Agriculture = 'Agriculture',
  Logistics_Transportation = 'Logistics & Transportation',
  Food_Production = 'Food Production',
  Restaurants_QSR_Fast_Casual_Full_Service = 'Restaurants (QSR / Fast Casual / Full Service)',
  Hospitality_Hotels_Resorts = 'Hospitality (Hotels / Resorts)',
  Catering_Events = 'Catering & Events',
  Retail = 'Retail',
  IT_Technology = 'IT / Technology',
  Engineering = 'Engineering',
  Finance_Accounting = 'Finance & Accounting',
  Marketing = 'Marketing',
  Legal = 'Legal',
  Education = 'Education',
}

export enum CompanyType {
  SoleProprietorship = 'Sole Proprietorship',
  GeneralPartnership = 'General Partnership',
  LimitedPartnership = 'Limited Partnership (LP)',
  LimitedLiabilityPartnership = 'Limited Liability Partnership (LLP)',
  LimitedLiabilityCompany = 'Limited Liability Company (LLC)',
  CCorporation = 'C Corporation',
  SCorporation = 'S Corporation',
  BenefitCorporation = 'Benefit Corporation',
  NonprofitCorporation = 'Nonprofit Corporation',
  Cooperative = 'Cooperative (Co-op)',
  JointVenture = 'Joint Venture (JV)',
}

export enum CompanyPayRollFrequency {
  Weekly = 'Weekly',
  BiWeekly = 'Bi-weekly',
  Monthly = 'Monthly',
  SemiMonthly = 'Semi-monthly',
}

export enum EntityStatus {
  Pending = 'Pending',
  Active = 'Active',
  Disabled = 'Disabled',
  Creating = 'Creating',
  Failed = 'Failed',
  Archived = 'Archived',
}

// Interfaces matching MSP NodeJS service
export interface Address {
  line1: string
  city: string
  state: string
  zipCode: string
  display?: string
}

export interface Region {
  id: string
  name: string
  description?: string
}

export interface Company {
  id: string
  _id?: string
  name: string
  phoneNumber: string
  phoneExtension?: string
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  companyAddress: Address
  billingAddress: Address
  agencyId: string
  mspId?: string
  companyWebsite?: string
  industry: IndustryType
  companyType: CompanyType
  paySchedule: CompanyPayRollFrequency // Updated from payRoll
  taxId?: string
  standardPayDaylag: number // Updated from paymentRollOutAfter
  payPeriodStartDay?: Date // Updated from startingDay
  payPeriodEndDay?: Date // Updated from endingDay
  status?: EntityStatus
  regions?: Region[]
  sites?: Site[]
  createdDate?: string
  modifiedDate?: string
  /** Lender Financing */
  isFactored?: boolean
  lenderId?: string
  factoringStartDate?: Date
  safetyCoordinator?: string
}

// Request DTOs
export interface CreateCompanyRequest {
  name: string
  phoneNumber: string
  phoneExtension?: string
  adminFirstName?: string
  adminLastName?: string
  adminEmail?: string
  companyAddress: Address
  billingAddress: Address
  agencyId: string
  mspId?: string
  companyWebsite?: string
  industry: IndustryType
  companyType: CompanyType
  paySchedule: CompanyPayRollFrequency // Updated from payRoll
  taxId?: string
  standardPayDaylag: number // Updated from paymentRollOutAfter
  payPeriodStartDay: string // Updated from startingDay
  payPeriodEndDay: string // Updated from endingDay
  timekeepingSystem?: string
  safetyCoordinator?: string
  /**
   * STFFLG-4182: the client's Order Access team, written in the same onboarding (a team failure
   * rolls the client back). Sent only by a caller who may set it (GET /api/team/edit-access during
   * create said yes): without the `teams` view and create permissions it is a 403. When sent it
   * needs a Host or a Co-host (else 400 "A team must have a Host or a Co-host…"), and everyone on
   * it must be assignable for `agencyId` (else 400, errorCode 'orderAccessIneligible'). Left out,
   * the client is created with no team. The response carries no team fields.
   */
  orderAccess?: UpsertTeamRequest
}

export interface CreateRegionRequest {
  name: string
  description?: string
}

export interface UpdateRegionRequest {
  name?: string
  description?: string
}

export interface UpdateCompanyRequest {
  id: string
  name?: string
  phoneNumber?: string
  phoneExtension?: string
  adminFirstName?: string | null
  adminLastName?: string | null
  adminEmail?: string | null
  companyAddress?: Address
  billingAddress?: Address
  companyWebsite?: string
  industry?: IndustryType
  companyType?: CompanyType
  paySchedule?: CompanyPayRollFrequency // Updated from payRoll
  taxId?: string | null
  standardPayDaylag?: number // Updated from paymentRollOutAfter
  payPeriodStartDay?: string // Updated from startingDay
  payPeriodEndDay?: string // Updated from endingDay
  agencyId?: string // Agency assignment
  mspId?: string
  payRollStartDate?: Date
  timekeepingSystem?: string
  safetyCoordinator?: string | null
  /** Lender Financing */
  isFactored?: boolean
  lenderId?: string | null
  factoringStartDate?: Date | null
}

export interface CompanyPagingModel {
  companies: Company[]
  totalCompanies: number
  currentPage: number
  totalPages: number
}

export interface CompanyNamesAndIds {
  id: string
  name: string
  /** The agency this company is directly assigned to (STFFLG-3968). Populated by
   *  the BE when it returns agencyId on the lightweight endpoint; safe to treat
   *  as optional so callers are backwards-compatible if the field is absent. */
  agencyId?: string
}

export interface RegionHierarchyResponse {
  id: string
  name: string
  sites: Site[]
}

export interface CompanyHierarchyResponse {
  id: string
  name: string
  numSites: number
  companySites: Site[]
  regions: RegionHierarchyResponse[]
  // Payroll configuration fields
  paySchedule?: string
  payPeriodStartDay?: string
  payPeriodEndDay?: string
  payRollStartDate?: string // ISO date string
  standardPayDaylag?: number
  // Agency assignment
  agencyId?: string
  agencyName?: string
  // EOR information for paycode lookups
  eor?: {
    id: string
    name: string
  }
  timekeepingSystem?: string
  safetyCoordinator?: string
}

export interface PagingParametersDto {
  pageNumber?: number
  pageSize?: number
  search?: string
}

export interface VerifyIfAgencyManageCompanyRequest {
  companyId: string
  agencyIds: string[]
}

export interface VerifyIfAgencyManageCompanyResponse {
  canManage: boolean
  managingAgencyId?: string
}

export interface GetCompaniesQueryDto extends PagingParametersDto {
  agencyId?: string
  status?: EntityStatus
  adminEmail?: string
  createdDate?: string // ISO date string
  name?: string
  /** STFFLG-3643: assigned agency name (partial, case-insensitive). */
  agencyName?: string
}

/**
 * Company API service for MSP NodeJS endpoints
 * Handles company CRUD operations, regions, and hierarchy
 */
export class CompanyApi extends BaseApi {
  /**
   * Get company name by ID
   * GET /api/company/getCompanyNameById/:id
   */
  async getCompanyNameById(id: string): Promise<string> {
    try {
      const response = await this.client.get<string>(`/api/company/getCompanyNameById/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching company name: ${id}`, error)
      throw error
    }
  }

  /**
   * Get paginated companies with enhanced filtering
   * GET /api/company/getCompanies
   */
  async getCompanies(query?: GetCompaniesQueryDto): Promise<CompanyPagingModel> {
    try {
      const response = await this.client.get<CompanyPagingModel>('/api/company/getCompanies', {
        params: query,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching companies:', error)
      throw error
    }
  }

  /**
   * Get all companies
   * GET /api/company/getAllCompanies
   * @param agencyId - Optional agency ID to filter companies (required for GA users)
   */
  async getAllCompanies(agencyId?: string): Promise<Company[]> {
    try {
      const response = await this.client.get<Company[]>('/api/company/getAllCompanies', {
        params: agencyId ? {agencyId} : undefined,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching all companies:', error)
      throw error
    }
  }

  /**
   * Verify if agency can manage company
   * POST /api/company/verifyIfAgencyManageCompany
   */
  async verifyIfAgencyManageCompany(
    request: VerifyIfAgencyManageCompanyRequest,
  ): Promise<VerifyIfAgencyManageCompanyResponse> {
    try {
      const response = await this.client.post<VerifyIfAgencyManageCompanyResponse>(
        '/api/company/verifyIfAgencyManageCompany',
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error verifying agency-company relationship:', error)
      throw error
    }
  }

  /**
   * Get company names and IDs
   * GET /api/company/getAllCompanyNamesAndIds
   * @param pagingParams - Optional paging parameters
   * @param agencyId - Optional agency ID to filter companies (required for GA users)
   */
  async getAllCompanyNamesAndIds(
    pagingParams?: PagingParametersDto,
    agencyId?: string,
    agencyIds?: string[],
  ): Promise<CompanyNamesAndIds[]> {
    try {
      // BE accepts comma-separated agencyIds via query param (Transform decorator
      // splits it into a string[] on the server). Avoid sending empty arrays.
      const agencyIdsParam = agencyIds && agencyIds.length > 0 ? agencyIds.join(',') : undefined
      const response = await this.client.get<CompanyNamesAndIds[]>(
        '/api/company/getAllCompanyNamesAndIds',
        {
          params: {...pagingParams, agencyId, agencyIds: agencyIdsParam},
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company names and IDs:', error)
      throw error
    }
  }

  /**
   * Get company by ID
   * GET /api/company/getCompany/:id
   */
  async getCompanyById(id: string): Promise<Company | null> {
    try {
      const response = await this.client.get<Company | null>(`/api/company/getCompany/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching company: ${id}`, error)
      throw error
    }
  }

  /**
   * Get company hierarchy
   * GET /api/company/getCompanyHierarchy/:id
   * @param id - Company ID
   * @param agencyId - Optional agency ID context (for multi-agency users)
   */
  async getCompanyHierarchy(id: string, agencyId?: string): Promise<CompanyHierarchyResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.client.get<CompanyHierarchyResponse>(
        `/api/company/getCompanyHierarchy/${id}`,
        {params},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching company hierarchy: ${id}`, error)
      throw error
    }
  }

  /**
   * Create new company
   * POST /api/company/onboardCompany
   */
  async createCompany(companyData: CreateCompanyRequest): Promise<Company> {
    try {
      const sanitizedData = sanitizePayload(companyData)
      const response = await this.client.post<Company>('/api/company/onboardCompany', sanitizedData)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating company:', error)
      throw error
    }
  }

  /**
   * Update company
   * PUT /api/company/updateCompany
   */
  async updateCompany(companyData: UpdateCompanyRequest): Promise<Company> {
    try {
      const sanitizedData = sanitizePayload(companyData)
      const response = await this.client.put<Company>('/api/company/updateCompany', sanitizedData)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating company:', error)
      throw error
    }
  }

  /**
   * Add region to company
   * PUT /api/company/addCompanyRegion
   */
  async addCompanyRegion(companyId: string, regionData: CreateRegionRequest): Promise<Company> {
    try {
      const sanitizedData = sanitizePayload(regionData)
      const response = await this.client.put<Company>(
        `/api/company/addCompanyRegion?companyId=${companyId}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error adding region to company: ${companyId}`, error)
      throw error
    }
  }

  /**
   * Update company region
   * PUT /api/company/updateCompanyRegion
   */
  async updateCompanyRegion(
    companyId: string,
    regionId: string,
    regionData: UpdateRegionRequest,
  ): Promise<Company> {
    try {
      const sanitizedData = sanitizePayload(regionData)
      const response = await this.client.put<Company>(
        `/api/company/updateCompanyRegion?companyId=${companyId}&regionId=${regionId}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating company region: ${companyId}/${regionId}`, error)
      throw error
    }
  }

  /**
   * Update company status (archive/activate)
   * PUT /api/company/updateCompanyStatus/:id/Status/:status
   */
  async updateCompanyStatus(id: string, status: EntityStatus): Promise<Company> {
    try {
      const response = await this.client.put<Company>(
        `/api/company/updateCompanyStatus/${id}/Status/${status}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating company status: ${id}`, error)
      throw error
    }
  }

  /**
   * Delete company
   * DELETE /api/company/deleteCompany
   */
  async deleteCompany(id: string): Promise<Company> {
    try {
      const response = await this.client.delete<Company>(`/api/company/deleteCompany?id=${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error deleting company: ${id}`, error)
      throw error
    }
  }

  /**
   * Get company by name
   * GET /api/company/getCompanyByName/:name
   */
  async getCompanyByName(name: string): Promise<Company> {
    try {
      const response = await this.client.get<Company>(`/api/company/getCompanyByName/${name}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching company by name: ${name}`, error)
      throw error
    }
  }

  /**
   * Get companies by industry type
   * GET /api/company/getCompaniesByIndustry/:industryType
   */
  async getCompaniesByIndustry(industryType: IndustryType): Promise<Company[]> {
    try {
      const response = await this.client.get<Company[]>(
        `/api/company/getCompaniesByIndustry/${industryType}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching companies by industry:', error)
      throw error
    }
  }

  /**
   * Get companies by status
   * GET /api/company/getCompaniesByStatus/:status
   */
  async getCompaniesByStatus(status: EntityStatus): Promise<Company[]> {
    try {
      const response = await this.client.get<Company[]>(
        `/api/company/getCompaniesByStatus/${status}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching companies by status:', error)
      throw error
    }
  }

  /**
   * Archive company
   * PUT /api/company/archiveCompany/:id
   */
  async archiveCompany(id: string): Promise<Company> {
    try {
      const response = await this.client.put<Company>(
        `/api/company/updateCompanyStatus/${id}/Status/${EntityStatus.Archived}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error archiving company: ${id}`, error)
      throw error
    }
  }

  /**
   * Restore archived company
   * PUT /api/company/restoreCompany/:id
   */
  async restoreCompany(id: string): Promise<Company> {
    try {
      const response = await this.client.put<Company>(
        `/api/company/updateCompanyStatus/${id}/Status/${EntityStatus.Active}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error restoring company: ${id}`, error)
      throw error
    }
  }

  /**
   * Get company names that have placed orders (for filter dropdowns)
   * GET /api/company/getCompanyNamesWithOrders
   *
   * Pass the agency selected in the agency switcher; without it the BE scopes to the
   * user's home agency, which for a multi-agency user is not the agency on screen.
   */
  async getCompanyNamesWithOrders(agencyId?: string): Promise<CompanyNamesAndIds[]> {
    try {
      const response = await this.client.get<CompanyNamesAndIds[]>(
        '/api/company/getCompanyNamesWithOrders',
        {params: agencyId ? {agencyId} : undefined},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching company names with orders:', error)
      throw error
    }
  }

  async getDepartmentRefsWithOrders(
    companyId: string,
    agencyId?: string,
  ): Promise<Array<{departmentId: string; siteId: string}>> {
    try {
      const response = await this.client.get<Array<{departmentId: string; siteId: string}>>(
        '/api/serviceorder/getDepartmentRefsWithServiceOrders',
        {params: {companyId, ...(agencyId && {agencyId})}},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching department references with orders:', error)
      throw error
    }
  }
}

export const companyApi = new CompanyApi()
